import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getTutor, type StaffAccountState } from "../api.js";
import { isOwner, useAuth } from "../auth.js";
import { formatDate, formatTime } from "../format.js";
import { useAsync } from "../useAsync.js";
import { TableScrollRegion } from "../ui/TableScrollRegion.js";
import { Pagination, usePagination } from "../Pagination.js";
import { DENSE_ROW_PAGE_SIZE } from "../paginationPageSizes.js";

/**
 * One tutor, reached by clicking their name in the diary.
 *
 * The question the diary raises is "who is teaching that, and what else do they
 * take?", so the page leads with who they teach and what is coming up. The
 * account state below it is the staff list's question rather than the diary's,
 * and is here because this is where somebody lands when they wonder why a tutor
 * has never written anything up.
 *
 * An owner reaches anybody's; a tutor reaches their own. The server decides —
 * see `canViewTutorProfile` — and refuses the rest with a 403, which arrives
 * here as an ordinary error rather than something this page has to anticipate.
 */

/** The same wording the staff list uses, so the two pages agree. */
const STATE_LABEL: Record<StaffAccountState, string | null> = {
  // The ordinary state, and saying so on a profile is noise.
  active: null,
  invited: "invitation sent, not yet accepted",
  "never-signed-in": "has never signed in",
  left: "no longer teaching here",
};

export function Tutor() {
  const { id = "" } = useParams();
  const { me } = useAuth();
  const { data, error, loading } = useAsync(() => getTutor(id), [id]);
  const [studentSearch, setStudentSearch] = useState("");
  const students = data?.students ?? [];
  const studentSearchQuery = studentSearch.trim().toLocaleLowerCase();
  const filteredStudents = useMemo(
    () =>
      studentSearchQuery === ""
        ? students
        : students.filter((student) =>
            `${student.fullName} ${student.instruments.join(" ")}`
              .toLocaleLowerCase()
              .includes(studentSearchQuery),
          ),
    [studentSearchQuery, students],
  );
  const studentPagination = usePagination(
    filteredStudents,
    DENSE_ROW_PAGE_SIZE,
    `${id}:${studentSearchQuery}`,
  );
  const rosterNeedsGuardrail = students.length > DENSE_ROW_PAGE_SIZE;

  if (loading) return <p className="empty">Loading…</p>;
  if (error) return <div className="error">{error}</div>;
  if (!data) return null;

  const { tutor, totals, upcoming } = data;
  const state = STATE_LABEL[tutor.state];

  return (
    <>
      <p className="small muted">
        <Link to="/diary">← Diary</Link>
      </p>

      <h1>{tutor.displayName}</h1>
      <p className="lede">
        {tutor.leftAt !== null
          ? `Taught here until ${formatDate(tutor.leftAt)}.`
          : totals.students === 1
            ? "Teaches one student here."
            : `Teaches ${totals.students} students here.`}
      </p>

      <div className="panel spread">
        <div>
          <div className="small muted">Lessons in the diary</div>
          <strong>{totals.lessons}</strong>
        </div>
        <div>
          <div className="small muted">Students</div>
          <strong>{totals.students}</strong>
        </div>
        <div>
          <div className="small muted">Lessons written up</div>
          <strong>{totals.notesWritten}</strong>
        </div>
        <div>
          {/* Worth its own figure rather than folded into the one above: a
              draft is written and unsent, so the family has seen nothing. */}
          <div className="small muted">Drafts unsent</div>
          <strong>{totals.drafts}</strong>
        </div>
      </div>

      <h2>Account</h2>
      <div className="panel">
        <TableScrollRegion label="Tutor account details" minWidth="420px">
          <table>
            <tbody>
              <tr>
                <th>Email</th>
                <td className="muted small">{tutor.email ?? "—"}</td>
              </tr>
              <tr>
                <th>Last signed in</th>
                <td className="muted small">
                  {tutor.lastLoginAt ? formatDate(tutor.lastLoginAt) : "never"}
                </td>
              </tr>
              <tr>
                <th>On the books since</th>
                <td className="muted small">{formatDate(tutor.createdAt)}</td>
              </tr>
              <tr>
                {/* What ties them to the bookings. Worth seeing when their diary
                  looks emptier than it should — a profile with no id here is
                  attached to nothing the sync brings in. */}
                <th>Booking system id</th>
                <td className="muted small">
                  {tutor.externalProviderId ?? "not linked"}
                </td>
              </tr>
              <tr>
                <th>Role</th>
                <td>
                  {tutor.roles.length === 0 ? (
                    <span className="muted small">—</span>
                  ) : (
                    tutor.roles.map((role) => (
                      <span className="tag" key={role}>
                        {role}
                      </span>
                    ))
                  )}
                  {state !== null && <span className="tag warn"> {state}</span>}
                </td>
              </tr>
            </tbody>
          </table>
        </TableScrollRegion>

        {/* Only an owner can act on any of this, and a link that always
            returns 403 is worse than no link. */}
        {isOwner(me) && (
          <p className="small muted">
            Inviting them, or marking them as having left, is done from{" "}
            <Link to="/staff">Staff</Link>.
          </p>
        )}
      </div>

      <h2>Coming up</h2>
      <div className="panel">
        {upcoming.length === 0 && <p className="empty">Nothing booked.</p>}
        {upcoming.map((booking) => (
          <div className="slot" key={booking.bookingId}>
            <time>{formatTime(booking.startsAt)}</time>
            <div style={{ flex: 1 }}>
              <div>
                {booking.studentId !== null ? (
                  <Link to={`/students/${booking.studentId}`}>
                    {booking.studentName}
                  </Link>
                ) : (
                  <span className="muted">
                    {booking.customerName ?? "Unknown"}{" "}
                    <span className="tag warn">not matched</span>
                  </span>
                )}
              </div>
              <div className="small muted">
                {formatDate(booking.startsAt)} ·{" "}
                {booking.serviceName ?? "Lesson"}
                {booking.isSchools && " · schools"}
              </div>
            </div>
          </div>
        ))}
        {data.upcomingHasMore && (
          <p className="small" style={{ marginBottom: 0 }}>
            <Link to="/diary">View more upcoming lessons in the Diary</Link>
          </p>
        )}
      </div>

      <h2>Students</h2>
      <div className="panel">
        {rosterNeedsGuardrail && (
          <label className="small" style={{ display: "grid", gap: 4, marginBottom: 12 }}>
            Search this tutor&rsquo;s students
            <input
              type="search"
              value={studentSearch}
              onChange={(event) => setStudentSearch(event.target.value)}
              placeholder="Search by name or instrument…"
              aria-label="Search this tutor's students"
            />
          </label>
        )}
        <TableScrollRegion label="Tutor students" minWidth="620px">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Learning</th>
                {/* Booked, not taken: the count includes the ones still ahead,
                  which is why a new student can read "2" and "none yet". */}
                <th>Lessons booked</th>
                <th>Last lesson</th>
              </tr>
            </thead>
            <tbody>
              {studentPagination.items.map((student) => (
                <tr key={student.id}>
                  <td>
                    <Link to={`/students/${student.id}`}>
                      {student.fullName}
                    </Link>
                  </td>
                  <td className="muted small">
                    {student.instruments.length > 0
                      ? student.instruments.join(" · ")
                      : "—"}
                  </td>
                  <td className="tabular">{student.lessons}</td>
                  <td className="muted small">
                    {/* Null when every lesson with them is still ahead, which is
                      a new student rather than a missing record. */}
                    {student.lastLesson === null
                      ? "none yet"
                      : formatDate(student.lastLesson)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScrollRegion>
        {studentPagination.items.length === 0 && (
          <p className="empty">
            {studentSearchQuery === ""
              ? "Nobody is booked in with them."
              : "No students match that search."}
          </p>
        )}
        {rosterNeedsGuardrail && (
          <Pagination
            page={studentPagination.page}
            pages={studentPagination.pages}
            ariaLabel="Tutor student roster pagination"
            onPrevious={studentPagination.previous}
            onNext={studentPagination.next}
          />
        )}
      </div>
    </>
  );
}
